import React, { useState, useRef, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { MoveRight, MoveLeft } from "lucide-react";
import axios from "axios";

const getAvatarColor = (name) => {
  const colors = [
    'bg-red-500',      // Red
    'bg-blue-500',     // Blue
    'bg-green-500',    // Green
    'bg-yellow-500',   // Yellow
    'bg-purple-500',   // Purple
    'bg-pink-500',     // Pink
    'bg-indigo-500',   // Indigo
    'bg-teal-500',     // Teal
    'bg-orange-500',   // Orange
    'bg-cyan-500',     // Cyan
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colorIndex = Math.abs(hash) % colors.length;
  return colors[colorIndex];
};

const getInitials = (name) => {
  if (!name) return "?";

  const parts = name.split(' ');
  if (parts.length === 1) {
    return name.charAt(0).toUpperCase();
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const ReviewCard = ({ review }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 120;

  const avatarColor = getAvatarColor(review.authorName);
  const initials = getInitials(review.authorName);

  return (
      <div className="px-0 px-lg-3">
        <div
            className={`relative bg-white rounded-lg p-6 transition-all duration-300 ${
                isExpanded ? "h-auto" : "h-64"
            }`}
        >
          <div className="absolute right-4 bg-red-600 p-2 rounded-sm">
            <svg
                className="w-5 h-5 text-white"
                viewBox="0 0 24 24"
                fill="currentColor"
            >
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
            </svg>
          </div>

          <div className="flex flex-col h-full">
            <div className="flex space-x-1 mb-4">
              {[...Array(5)].map((_, index) => (
                  <div key={index} className="text-yellow-400 text-lg">
                    {index < review.rating ? "★" : ""}
                  </div>
              ))}
            </div>

            <div className="flex-grow">
              <p className="text-gray-700 leading-relaxed">
                {isExpanded
                    ? review.text
                    : review.text.length > maxLength
                        ? review.text.substring(0, maxLength) + "..."
                        : review.text}
              </p>
              {review.text.length > maxLength && (
                  <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="text-blue-600 hover:text-blue-800 mt-2 text-sm font-medium"
                  >
                    {isExpanded ? "Read Less" : "Read More"}
                  </button>
              )}
            </div>

            <div className="mt-4 pt-2 border-t border-gray-200">
              <div className="flex items-center">
                {review.profilePhotoUrl && !review.profilePhotoUrl.includes("default-user") ? (
                    <img
                        src={review.profilePhotoUrl}
                        alt={review.authorName}
                        className="w-10 h-10 rounded-full mr-3 object-cover"
                    />
                ) : (
                    <div className={`w-10 h-10 ${avatarColor} rounded-full flex items-center justify-center text-white font-medium mr-3`}>
                      {initials}
                    </div>
                )}
                <span className="font-medium text-gray-800">{review.authorName}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

const ReviewSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://api.mesprocedures.fr/google-reviews');
        setReviews(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError('Failed to load reviews. Please try again later.');
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    adaptiveHeight: false,
    pauseOnHover: true,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  if (loading) {
    return (
        <div id="avis" className="aviSlides pt-2">
          <div className="container mx-auto pt-10 text-center">
            Loading reviews...
          </div>
        </div>
    );
  }

  if (error) {
    return (
        <div id="avis" className="aviSlides pt-2">
          <div className="container mx-auto pt-10 text-center text-red-500">
            {error}
          </div>
        </div>
    );
  }

  return (
      <div id="avis" className="aviSlides pt-2">
        <div className="container mx-auto pt-10">
          <div className="flex flex-col md:flex-row justify-between pt-2 space-y-0 md:space-y-0">
            <div className="flex items-center justify-center flex-1 md:mt-0">
              <img
                  className="w-2/3 object-contain -mt-2"
                  src="/assets/img/theme/avis-verifie.png"
                  alt="avis vérifié"
                  loading="lazy"
              />
            </div>
            <div className="flex items-center justify-center flex-1 md:mt-0">
              <img
                  className="w-2/3 object-contain -mt-4"
                  src="/assets/img/theme/avis-trustpilot.png"
                  alt="trustpilot"
                  loading="lazy"
              />
            </div>
            <div className="flex items-center justify-center flex-1 md:mt-0">
              <img
                  className="w-2/3 object-contain -mt-4"
                  src="/assets/img/theme/avis_google.png"
                  alt="google review"
                  loading="lazy"
              />
            </div>
          </div>

          <div className="mx-auto py-8">
            {reviews.length > 0 ? (
                <>
                  <Slider ref={sliderRef} {...settings}>
                    {reviews.map((review, index) => (
                        <ReviewCard key={review.id || index} review={review} />
                    ))}
                  </Slider>

                  <div className="flex items-center justify-center mt-2 space-x-6 text-blue-900 text-lg pt-4">
                    <button
                        onClick={() => sliderRef.current.slickPrev()}
                        className="text-blue-900 hover:text-blue-700 transition"
                    >
                      <MoveLeft />
                    </button>
                    <span className="font-medium">
                  {currentSlide + 1} / {reviews.length}
                </span>
                    <button
                        onClick={() => sliderRef.current.slickNext()}
                        className="text-blue-900 hover:text-blue-700 transition"
                    >
                      <MoveRight />
                    </button>
                  </div>
                </>
            ) : (
                <div className="text-center py-10 text-gray-500">
                  No reviews available at the moment.
                </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default ReviewSlider;